import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "./ui/card";
import { AlertCircle } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import API_CONFIG from "../lib/ApiConfig";
import dollar from "../assets/images/dollar.svg";
import analyticsService from "../lib/AnalyticsService";
import { useInteractionTracking } from "../hooks/useAnalytics";

const ContactInfoScreen = ({ userData, onComplete, onBack }) => {
  const { user, getIdToken } = useAuth();
  const { trackButtonClick } = useInteractionTracking();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    address: "",
    unit: "", // Added unit/apartment field
    email: "",
    phone: "",
  });

  // Store the structured address components
  const [addressComponents, setAddressComponents] = useState({
    addressLine1: "",
    city: "",
    state: "",
    zip: "",
    country: "",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(true);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [placesAutocomplete, setPlacesAutocomplete] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [authMethod, setAuthMethod] = useState(null); // 'phone' or 'email'

  const addressInputRef = useRef(null);

  useEffect(() => {
    const isValid = validateForm();
    setIsFormValid(isValid);
  }, [formData, addressComponents, authMethod]);

  const constructFullAddress = (addressComponents) => {
    if (!addressComponents) return "";

    const parts = [];

    if (addressComponents.addressLine1)
      parts.push(addressComponents.addressLine1);
    if (addressComponents.addressLine2)
      parts.push(addressComponents.addressLine2);
    if (addressComponents.city) parts.push(addressComponents.city);
    if (addressComponents.state) parts.push(addressComponents.state);
    if (addressComponents.zip) parts.push(addressComponents.zip);

    return parts.join(", ");
  };

  // Fetch user data from server
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setUserLoading(true);
        // Get Firebase token
        const token = await getIdToken();
        if (!token) {
          throw new Error("Authentication required");
        }

        // Call your API to get user profile
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/users/me`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": token,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch user data: ${response.status}`);
        }

        const userData = await response.json();
        console.log("userData", userData);

        // Update form with user data
        setFormData({
          firstName: userData.firstName || "",
          lastName: userData.lastName || "",
          address: constructFullAddress(userData.address), // Construct from components
          unit: userData.address?.addressLine2 || "",
          email: userData.email || "",
          phone: userData.phone?.startsWith("+1")
            ? userData.phone.substring(2)
            : userData.phone || "",
        });

        // Update address components if available
        if (userData.address) {
          setAddressComponents({
            addressLine1: userData.address.addressLine1 || "",
            city: userData.address.city || "",
            state: userData.address.state || "",
            zip: userData.address.zip || "",
            country: userData.address.country || "",
          });
        }

        // Determine auth method based on user data
        if (userData.authMethod) {
          setAuthMethod(userData.authMethod);
        } else if (user) {
          // Fallback to Firebase user object
          setAuthMethod(user.phoneNumber ? "phone" : "email");
        }
      } catch (err) {
        console.error("Error fetching user data:", err);

        // Fallback to session storage or Firebase user
        handleFallbackData();
      } finally {
        setUserLoading(false);
      }
    };

    if (user) {
      fetchUserData();
    } else {
      handleFallbackData();
      setUserLoading(false);
    }
  }, [user, getIdToken]);

  // Fallback to session storage or Firebase user data
  const handleFallbackData = () => {
    // First check if we have contact info data in session storage
    const savedData = sessionStorage.getItem("contactInfoData");
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setFormData((prev) => ({
          ...prev,
          firstName: parsedData.firstName || "",
          lastName: parsedData.lastName || "",
          address: parsedData.address || "",
          unit: parsedData.unit || "",
          email: parsedData.email || "",
          phone: parsedData.phone || "",
        }));
        return;
      } catch (err) {
        console.error("Error parsing saved contact info:", err);
      }
    }

    // Otherwise use userData or Firebase user data
    if (userData?.firstName || userData?.lastName || userData?.address) {
      setFormData((prev) => ({
        ...prev,
        firstName: userData?.firstName || "",
        lastName: userData?.lastName || "",
        address: userData?.address || "",
        unit: userData?.unit || "",
        email: userData?.email || "",
        phone: userData?.phone || "",
      }));
    } else if (user) {
      // Determine auth method
      setAuthMethod(user.phoneNumber ? "phone" : "email");

      // Set email or phone from Firebase user
      if (user.email) {
        setFormData((prev) => ({ ...prev, email: user.email }));
      }
      if (user.phoneNumber) {
        // Strip +1 if present
        const phoneNumber = user.phoneNumber.startsWith("+1")
          ? user.phoneNumber.substring(2)
          : user.phoneNumber;
        setFormData((prev) => ({ ...prev, phone: phoneNumber }));
      }

      // Try to parse display name into first and last name
      if (user.displayName) {
        const nameParts = user.displayName.split(" ");
        if (nameParts.length > 1) {
          setFormData((prev) => ({
            ...prev,
            firstName: nameParts[0] || "",
            lastName: nameParts.slice(1).join(" ") || "",
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            firstName: user.displayName || "",
          }));
        }
      }
    }
  };

  // Load Google Maps API script
  useEffect(() => {
    // Check if script is already loaded
    if (window.google && window.google.maps) {
      setGoogleMapsLoaded(true);
      return;
    }

    // Function to initialize Google Maps
    window.initGoogleMapsAutocomplete = () => {
      setGoogleMapsLoaded(true);
    };

    // Load the Google Maps script
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDG7_6lxN0J7dEDP1maUwBzoOlWd0wqpFQ&libraries=places&callback=initGoogleMapsAutocomplete`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    return () => {
      // Clean up
      if (placesAutocomplete) {
        window.google.maps.event.clearInstanceListeners(placesAutocomplete);
      }
      delete window.initGoogleMapsAutocomplete;
    };
  }, []);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (googleMapsLoaded && addressInputRef.current) {
      try {
        const autocomplete = new window.google.maps.places.Autocomplete(
          addressInputRef.current,
          { types: ["address"] }
        );

        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          if (place.formatted_address) {
            setFormData((prev) => ({
              ...prev,
              address: place.formatted_address,
            }));

            // Extract address components
            const addressData = extractAddressComponents(place);
            setAddressComponents(addressData);

            // Clear any address errors when valid address is selected
            setErrors((prev) => ({ ...prev, address: null }));
          }
        });

        setPlacesAutocomplete(autocomplete);
      } catch (error) {
        console.error("Error initializing Google Maps Autocomplete:", error);
      }
    }
  }, [googleMapsLoaded, addressInputRef.current]);

  // Extract address components from Google Place result
  const extractAddressComponents = (place) => {
console.log("-------------------- extractAddressComponents -------------------")
    const components = {
      streetNumber: "",
      route: "",
      addressLine1: "",
      city: "",
      state: "",
      zip: "",
      country: "",
    };

    if (place.address_components) {
      for (const component of place.address_components) {
        const types = component.types;

        if (types.includes("street_number")) {
          components.streetNumber = component.long_name;
        } else if (types.includes("route")) {
          components.route = component.long_name;
        } else if (types.includes("locality")) {
          components.city = component.long_name;
        } else if (types.includes("administrative_area_level_1")) {
          components.state = component.short_name;
        } else if (types.includes("postal_code")) {
          components.zip = component.long_name;
        } else if (types.includes("country")) {
          components.country = component.long_name;
        }
      }
    }

    // Combine street number and route for addressLine1
    if (components.streetNumber && components.route) {
      components.addressLine1 = `${components.streetNumber} ${components.route}`;
    } else if (components.route) {
      components.addressLine1 = components.route;
    } else {
      components.addressLine1 = place.formatted_address;
    }

    return components;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "address") {
      // Reset address components when typing
      setAddressComponents({
        addressLine1: "",
        city: "",
        state: "",
        zip: "",
        country: "",
      });
    }
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    } else if (
      !addressComponents.addressLine1 ||
      !addressComponents.city ||
      !addressComponents.state ||
      !addressComponents.zip
    ) {
      newErrors.address =
        "Please select a valid address with street, city, state, and ZIP";
    }

    // Validate email if user authenticated with phone
    if (authMethod === "phone" && !formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (
      authMethod === "phone" &&
      formData.email.trim() &&
      !isValidEmail(formData.email)
    ) {
      newErrors.email = "Please enter a valid email address";
    }

    // Validate phone if user authenticated with email
    if (authMethod === "email" && !formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (
      authMethod === "email" &&
      formData.phone.trim() &&
      !isValidPhone(formData.phone)
    ) {
      newErrors.phone = "Please enter a valid 10-digit phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Email validation
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Phone validation
  const isValidPhone = (phone) => {
    return /^\d{10}$/.test(phone);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    // Store form data in session storage
    sessionStorage.setItem("contactInfoData", JSON.stringify(formData));
    sessionStorage.setItem("contactInfoAddressComponents", JSON.stringify(addressComponents))

    try {
      // Get Firebase token
      const token = await getIdToken();
      if (!token) {
        throw new Error("Authentication required");
      }

      analyticsService.peopleSet(user.uid, {
          name: formData.firstName + ' ' + formData.lastName,
          email: formData.email,
          phone: formData.phone,
      });

      // Prepare data for API
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        isEmailVerified: authMethod === "email",
        isPhoneVerified: authMethod === "phone",
        address: {
          addressLine1: addressComponents.addressLine1,
          addressLine2: formData.unit,
          city: addressComponents.city,
          state: addressComponents.state,
          zip: addressComponents.zip,
          country: addressComponents.country,
          fullAddress: formData.address,
        },
        email: formData.email,
        phone: formData.phone,
      };

      // Update user data on server
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/users/update-me`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": token,
          },
          body: JSON.stringify(userData),
        }
      );

      if (!response.ok) {
        let errorMessage = `Failed to update user data: ${response.status}`;

        try {
          const errorData = await response.json();

          // Check if we have the error_code structure
          if (errorData && errorData.error_code) {
            // Map specific error codes to user-friendly messages
            switch (errorData.error_code) {
              case "ERROR_USER_EXISTS":
                errorMessage = "A user already exists with this phone or email";
                break;
              // Add more error codes here as needed
              default:
                // Use server message if available, otherwise use error code
                errorMessage = errorData.error_message || errorData.error_code;
            }
          } else if (errorData && errorData.error_message) {
            // If no error_code but has error_message
            errorMessage = errorData.error_message;
          }
        } catch (jsonError) {
          // If JSON parsing fails, keep the default error message
          console.error("Error parsing error response:", jsonError);
        }

        throw new Error(errorMessage);
      }

      // Get updated user data
      const updatedUser = await response.json();

      // Complete this step with the updated user data
      if (onComplete) {
        onComplete(updatedUser);
      }
    } catch (error) {
      console.error("Error updating user data:", error);
      setErrors({
        general:
          error.message || "Failed to save your information. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (userLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
          <span className="ml-3">Loading your information...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back button if onBack is provided */}
      {/* {onBack && (
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </button>
      )} */}

      {/* Progress Overview */}
      {/* <Card className="bg-blue-50 border-blue-100">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Check className="w-5 h-5 text-green-500 mt-1" />
            <div>
              <p className="font-medium text-blue-900">Contact Information</p>
              <p className="text-sm text-blue-700">
                Please provide your basic contact information
              </p>
            </div>
          </div>
        </CardContent>
      </Card> */}

      {/* Contact Information Form */}
      <Card className="border-none">
        <div className="p-4 pb-6 text-center">
          <h1 className="text-xl uppercase font-bold mb-5">
            Confirm your Profile Information
          </h1>
          <p className="text-base text-darkGrey">
            We use this info to personalize your car profile and connect
            you with the best-fitting options
          </p>
        </div>
        <Card className="shadow-none bg-[#CEC5FF66] border-none mx-4 !mt-0 rounded-[20px] !mb-6">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <img src={dollar} alt="dollar" />
              <p className="text-base text-darkPurple">
                Great news! Your Ownli Insurance Profile could earn you{" "}
                <b className="uppercase">up to $5!</b>
              </p>
            </div>
          </CardContent>
        </Card>
        {/* <CardHeader>
          <CardTitle>Your Information</CardTitle>
        </CardHeader> */}
        <CardContent className="space-y-4 px-4">
          {/* Name Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <label
                htmlFor="firstName"
                className="text-sm font-medium text-darkGrey"
              >
                First Name <span className="text-red-500">*</span>
              </label>
              <div className="relative !mt-1">
                {/* <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" /> */}
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={`w-full font-medium text-darkGrey p-3 border rounded-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.firstName ? "border-red-500" : "border-grey"
                  }`}
                  placeholder="First Name"
                />
              </div>
              {errors.firstName && (
                <p className="text-red-500 text-sm">{errors.firstName}</p>
              )}
            </div>

            <div className="space-y-2">
              <label
                htmlFor="lastName"
                className="text-sm font-medium text-darkGrey"
              >
                Last Name <span className="text-red-500">*</span>
              </label>
              <div className="relative !mt-1">
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={`w-full font-medium text-darkGrey p-3 border rounded-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.lastName ? "border-red-500" : "border-grey"
                  }`}
                  placeholder="Last Name"
                />
              </div>
              {errors.lastName && (
                <p className="text-red-500 text-sm">{errors.lastName}</p>
              )}
            </div>
          </div>

          {/* Address Field with Google Autocomplete */}
          <div className="space-y-2">
            <label
              htmlFor="address"
              className="text-sm font-medium text-darkGrey"
            >
              Address <span className="text-red-500">*</span>
            </label>
            <div className="relative !mt-1">
              {/* <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" /> */}
              <input
                id="address"
                name="address"
                type="text"
                ref={addressInputRef}
                value={formData.address}
                onChange={handleInputChange}
                className={`w-full font-medium text-darkGrey p-3 border rounded-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.address ? "border-red-500" : "border-grey"
                }`}
                placeholder="Current address"
              />
            </div>
            {!googleMapsLoaded && (
              <div className="text-xs text-gray-500">
                Loading Google Maps...
              </div>
            )}
            {errors.address && (
              <p className="text-red-500 text-sm">{errors.address}</p>
            )}
          </div>

          {/* Unit/Apartment Field */}
          {/* <div className="space-y-2">
            <label htmlFor="unit" className="text-sm font-medium text-gray-700">
              Apartment/Unit/Suite (optional)
            </label>
            <input
              id="unit"
              name="unit"
              type="text"
              value={formData.unit}
              onChange={handleInputChange}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
              placeholder="Apt #, Unit #, etc."
            />
          </div> */}

          {/* Contact Fields (conditional based on auth method) */}
          <div className="grid grid-cols-1 gap-5">
            {/* Email Field */}
            <div className="space-y-2">
              <label
                htmlFor="email"
                className="text-sm font-medium text-darkGrey flex items-center gap-2"
              >
                Email
                {authMethod === "email" && (
                  <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                    Verified
                  </span>
                )}
                {authMethod === "phone" && (
                  <span className="text-red-500">*</span>
                )}
              </label>
              <div className="relative !mt-1">
                {/* <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" /> */}
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full font-medium text-darkGrey p-3 border rounded-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.email ? "border-red-500" : "border-grey"
                  }`}
                  placeholder="Email"
                  readOnly={authMethod === "email"}
                  disabled={authMethod === "email"}
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-sm">{errors.email}</p>
              )}
            </div>

            {/* Phone Field */}
            <div className="space-y-2">
              <label
                htmlFor="phone"
                className="text-sm font-medium text-darkGrey flex items-center gap-2"
              >
                Phone Number
                {authMethod === "phone" && (
                  <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                    Verified
                  </span>
                )}
                {authMethod === "email" && (
                  <span className="text-red-500">*</span>
                )}
              </label>
              <div className="relative !mt-1">
                {/* <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" /> */}
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => {
                    // Only allow digits
                    const value = e.target.value.replace(/\D/g, "");
                    setFormData((prev) => ({ ...prev, phone: value }));
                    if (errors.phone) {
                      setErrors((prev) => ({ ...prev, phone: null }));
                    }
                  }}
                  className={`w-full font-medium text-darkGrey p-3 border rounded-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.phone ? "border-red-500" : "border-grey"
                  }`}
                  placeholder="Phone number"
                  maxLength={10}
                  readOnly={authMethod === "phone"}
                  disabled={authMethod === "phone"}
                />
              </div>
              {errors.phone && (
                <p className="text-red-500 text-sm">{errors.phone}</p>
              )}
            </div>
          </div>

          {/* Error Display */}
          {errors.general && (
            <div className="flex items-start gap-2 p-3 bg-red-50 text-red-800 rounded-lg text-sm">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p>{errors.general}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={() => {
              trackButtonClick("save-contact", {
                step_name: "contact-info",
              });
              handleSubmit();
            }}
            disabled={isLoading || !isFormValid}
            className={`w-full flex items-center bg-purpleGradient text-white justify-center gap-2 py-3 px-4 rounded-lg transition-colors disabled:bg-none disabled:bg-gray-300`}
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <span className="font-bold">Next</span>
                {/* <ArrowRight className="w-4 h-4" /> */}
              </>
            )}
          </button>
          <button
            onClick={onBack}
            className="w-full uppercase h-12 border-2 border-darkGrey font-bold text-darkGrey px-4 rounded-xl transition-colors mt-4"
          >
            Back
          </button>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactInfoScreen;
